import { type FC } from 'react'
import RjsfForm, { type IChangeEvent } from '@rjsf/core'
import { type RJSFSchema } from '@rjsf/utils'
import { type AccountOpeningSchemaFormPanel } from '../../../models/AccountOpeningDescriptor'
import { accountSettingsHeading } from '../descriptor/stepCopy'
import { applyPanelChange, itemCitations, itemSchemaFetchedAt, itemValues, panelSchema, type ResolvedItem } from '../domain/collections'
import { HintText } from './flowPrimitives'
import { missingFieldContext, type MissingItem } from '../domain/missingFields'
import { asOfText } from '../domain/asOf'
import { useAccountTimezone } from '../../../common/useAccountTimezone'
import { useWizardFlow } from '../hooks/WizardFlowContext'
import { WIZARD_TEMPLATES, WIZARD_WIDGETS } from '../rjsf/wizardRjsfTheme'
import { WIZARD_DEFAULTS_BEHAVIOR, wizardValidator } from '../rjsf/wizardValidator'

interface PanelSchemaFormProps {
  item: ResolvedItem
  panel: AccountOpeningSchemaFormPanel
  missing: MissingItem[]
  readOnly?: boolean
  idPrefix: string
  onValuesChange: (values: Record<string, unknown>) => void
}

export const PanelSchemaForm: FC<PanelSchemaFormProps> = ({ item, panel, missing, readOnly: lockedByItem = false, idPrefix, onValuesChange }) => {
  const { readOnly: lockedByFlow, itemsCopy } = useWizardFlow()
  const readOnly = lockedByItem || lockedByFlow
  const schema = panelSchema(item, panel)
  const properties = (schema.properties as Record<string, unknown> | undefined) ?? {}
  const accountTimeZone = useAccountTimezone()
  const sourceNote = asOfText(panel.sourceNote, itemSchemaFetchedAt(item), accountTimeZone)

  if (Object.keys(properties).length === 0) {
    return panel.emptyHint ? <HintText>{panel.emptyHint}</HintText> : null
  }

  const heading = panel.heading ?? (panel.include ? undefined : accountSettingsHeading(schema, itemsCopy))
  const formData = itemValues(item)

  return (
    <div className='d-flex flex-column zocks-gap-1'>
      {heading ? <p className='paragraph-medium medium mb-0'>{heading}</p> : null}
      {item.schemaNote ? <HintText>{item.schemaNote}</HintText> : null}
      <div className='d-flex flex-column zocks-gap-0.5'>
        <RjsfForm
          idPrefix={idPrefix}
          schema={schema as RJSFSchema}
          formData={formData}
          validator={wizardValidator}
          widgets={WIZARD_WIDGETS}
          templates={WIZARD_TEMPLATES}
          formContext={{ ...missingFieldContext(readOnly ? [] : missing), formData, idPrefix, citations: itemCitations(item) }}
          disabled={readOnly}
          experimental_defaultFormStateBehavior={WIZARD_DEFAULTS_BEHAVIOR}
          onChange={(event: IChangeEvent) => onValuesChange(applyPanelChange(item, panel, event.formData))}
        >
          <></>
        </RjsfForm>
        {sourceNote ? <HintText className='text-end'>{sourceNote}</HintText> : null}
      </div>
    </div>
  )
}
